import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { SettingsCard } from '../../components/composed';
import { Button, TextField } from '../../components/primitives';
import { AddressCard } from '../../components/domain/address';
import { FacilityBoolean, FacilityBooleanForm } from '../../components/domain/facility';
import { ApprovalBanner, AreYouSure } from '../../components/domain/object_management';
import Resource from './Resource';
import Events from '../Events/Events';
import { getCsrfToken } from '../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const FacilityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRemoved, setIsRemoved] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Address editing state
  const [addressFormOpen, setAddressFormOpen] = useState(false);
  
  // Amenities editing state
  const [amenitiesFormOpen, setAmenitiesFormOpen] = useState(false);

  // Deny confirmation dialog state
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchFacility(id);
    }
  }, [id]);

  const fetchFacility = async (facilityId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/facilities/${facilityId}/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Facility not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFacility(data);
    } catch (err) {
      console.error('Error fetching facility:', err);
      setError(err.message || 'Failed to load facility. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/facilities');
  };

  const handleApprove = async () => {
    try {
      setUpdating(true);
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/facilities/${id}/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ is_approved: true }),
      });

      if (!response.ok) {
        throw new Error('Failed to approve facility');
      }

      const data = await response.json();
      setFacility(data);
    } catch (err) {
      console.error('Error approving facility:', err);
      setError('Failed to approve facility. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeny = () => {
    setDenyDialogOpen(true);
  };

  const handleDenyCancel = () => {
    setDenyDialogOpen(false);
  };

  const handleDenyConfirm = async () => {
    try {
      setUpdating(true);
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/facilities/${id}/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ is_active: false }),
      });

      if (!response.ok) {
        throw new Error('Failed to deny facility');
      }

      setDenyDialogOpen(false);
      setIsRemoved(true);
    } catch (err) {
      console.error('Error denying facility:', err);
      setError('Failed to deny facility. Please try again.');
      setDenyDialogOpen(false);
    } finally {
      setUpdating(false);
    }
  };

  const handleEditAddress = () => {
    setAddressFormOpen(true);
  };

  const handleAddressSave = (updatedFacility) => {
    setFacility(updatedFacility);
    setAddressFormOpen(false);
  };

  const handleEditAmenities = () => {
    setAmenitiesFormOpen(true);
  };

  const handleAmenitiesSave = (updatedFacility) => {
    setFacility(updatedFacility);
    setAmenitiesFormOpen(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Facility Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Facility Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
              <Button variant="outlined" onClick={handleBack}>
                Back to Facilities
              </Button>
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  if (!facility && !isRemoved) {
    return null;
  }

  // Show removed message if facility was denied
  if (isRemoved) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Facility Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                This facility has been removed.
              </Typography>
              <Button variant="outlined" onClick={handleBack}>
                Back to Facilities
              </Button>
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  const address = facility.address || {};

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        padding: 1,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <SettingsCard title={facility.name || "Facility Details"} sx={{ mb: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Approval Banner - only shown for unverified facilities */}
            {!facility.is_approved && (
              <ApprovalBanner
                message="This facility needs review."
                onApprove={handleApprove}
                onDeny={handleDeny}
                disabled={updating}
              />
            )}

            <Box>
              <Button variant="outlined" onClick={handleBack} sx={{ mb: 3 }}>
                Back to Facilities
              </Button>
            </Box>

            <Grid container spacing={4}>
              {/* Left Column - Location */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Map Placeholder */}
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1,
                    }}
                  />

                  {/* Address Card */}
                  <AddressCard
                    address={address}
                    title={facility.name}
                    onEdit={handleEditAddress}
                    showDefaultRadius={false}
                    elevation={0}
                    // sx={{ border: 'none' }}
                  />
                </Box>
              </Grid>

              {/* Right Column - Facility Information */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Facility Information
                    </Typography>
                    <Edit sx={{ fontSize: 18, color: 'text.secondary', cursor: 'pointer' }} />
                  </Box>

                  {/* Amenities Section */}
                  <FacilityBoolean
                    facility={facility}
                    columns={2}
                    onEdit={handleEditAmenities}
                  />

                  {/* Resources Section */}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Resources
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Website URL
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {facility.url ? (
                            <a href={facility.url} target="_blank" rel="noopener noreferrer">
                              {facility.url}
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Schedule URL
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {facility.schedule_url ? (
                            <a href={facility.schedule_url} target="_blank" rel="noopener noreferrer">
                              {facility.schedule_url}
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Created
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {facility.created_at
                            ? new Date(facility.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Last Updated
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {facility.updated_at
                            ? new Date(facility.updated_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </SettingsCard>

        {/* Resources Section */}
        <Resource facilityId={facility.id} />

        {/* Events Section */}
        <Events facilityId={facility.id} />
      </Box>

      {/* Facility Address Edit Dialog */}
      <FacilityAddressForm
        open={addressFormOpen}
        onClose={() => setAddressFormOpen(false)}
        onSave={handleAddressSave}
        facility={facility}
      />

      {/* Facility Amenities Edit Dialog */}
      <FacilityBooleanForm
        open={amenitiesFormOpen}
        onClose={() => setAmenitiesFormOpen(false)}
        onSave={handleAmenitiesSave}
        facility={facility}
      />

      {/* Deny Confirmation Dialog */}
      <Dialog open={denyDialogOpen} onClose={handleDenyCancel} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <AreYouSure
            message="Are you sure you want to deny this item?"
            onDelete={handleDenyConfirm}
            onCancel={handleDenyCancel}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

/**
 * FacilityAddressForm - Dialog for editing facility address
 */
const FacilityAddressForm = ({ open, onClose, onSave, facility }) => {
  const [formData, setFormData] = useState({
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Populate form when dialog opens
  useEffect(() => {
    if (open && facility?.address) {
      setFormData({
        address_1: facility.address.address_1 || '',
        address_2: facility.address.address_2 || '',
        city: facility.address.city || '',
        state: facility.address.state || '',
        zip_code: facility.address.zip_code || '',
        country: facility.address.country || 'United States',
      });
      setErrors({});
      setErrorMessage('');
    }
  }, [open, facility]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setErrors({});

    try {
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/facilities/${facility.id}/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ address: formData }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.address) {
          setErrors(data.address);
        } else {
          setErrorMessage(data.error || data.message || 'Failed to update address. Please try again.');
        }
        setLoading(false);
        return;
      }

      onSave(data);
      setLoading(false);
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      console.error('Error updating facility address:', err);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Facility Address</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Address 1"
            name="address_1"
            value={formData.address_1}
            onChange={handleChange}
            error={!!errors.address_1}
            helperText={errors.address_1}
            required
            margin="normal"
            autoComplete="street-address"
            autoFocus
          />

          <TextField
            fullWidth
            label="Address 2"
            name="address_2"
            value={formData.address_2}
            onChange={handleChange}
            error={!!errors.address_2}
            helperText={errors.address_2}
            margin="normal"
            autoComplete="address-line2"
          />

          <TextField
            fullWidth
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            error={!!errors.city}
            helperText={errors.city}
            required
            margin="normal"
            autoComplete="address-level2"
          />

          <TextField
            fullWidth
            label="State"
            name="state"
            value={formData.state}
            onChange={handleChange}
            error={!!errors.state}
            helperText={errors.state}
            required
            margin="normal"
            autoComplete="address-level1"
          />

          <TextField
            fullWidth
            label="Zip Code"
            name="zip_code"
            value={formData.zip_code}
            onChange={handleChange}
            error={!!errors.zip_code}
            helperText={errors.zip_code}
            required
            margin="normal"
            autoComplete="postal-code"
          />

          <TextField
            fullWidth
            label="Country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            error={!!errors.country}
            helperText={errors.country}
            margin="normal"
            autoComplete="country-name"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FacilityDetail;
